/**
 * SearchOverlay Component
 *
 * Full-page search overlay matching breitling-storefront-next pattern:
 * - White background, full-page scrollable
 * - Title centered, search input below
 * - Retailer link with map pin icon
 * - Suggested searches from collection facets
 * - Products grid below (watches)
 * - Close button absolutely positioned (desktop)
 */

'use client';

import { ProductCard, type ProductCardData } from '@/components/brand';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { getAddToCartTrackingId, useAddToCart } from '@/hooks/useAddToCart';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSearchAutocomplete } from '@/hooks/useSearchAutocomplete';
import type { ProductHit } from '@/lib/algolia/types';
import { searchPaths } from '@/lib/paths';
import { transformHitsToProductCards } from '@/lib/search';
import {
  SEARCH_OVERLAY_INITIAL_PRODUCTS_COMPACT,
  SEARCH_OVERLAY_INITIAL_PRODUCTS_WIDE,
  SEARCH_OVERLAY_PRODUCTS_PER_PAGE,
} from '@/lib/search/constants';
import { getGalletPlpProductCardProps } from '@/lib/product/galletPlpProductCardProps';
import { searchStateActions, useSearchState } from '@/states/searchState';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { Link, useRouter } from '@/lib/i18n/navigation';
import { useParams, usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SearchDropdown } from '../SearchDropdown';
import { SearchInput } from '../SearchInput';
import styles from './SearchOverlay.module.scss';

export interface SearchOverlayProps {
  /** CMS-provided data for customization */
  cmsData?: {
    title?: string;
    placeholder?: string;
    showRetailers?: boolean;
  };
  /** Server-fetched initial products (from orchestrator) */
  initialProducts?: ProductCardData[];
  /** Server-fetched collections for suggested searches (from orchestrator) */
  initialCollections?: string[];
  /** Server-fetched total product count (from orchestrator) */
  initialTotalProducts?: number;
}

export function SearchOverlay({
  cmsData,
  initialProducts,
  initialCollections,
  initialTotalProducts,
}: SearchOverlayProps) {
  const t = useTranslations('search');
  const tBadges = useTranslations('product.badges');
  const { overlayStatus } = useSearchState();
  const params = useParams();
  const pathname = usePathname();
  const router = useRouter();
  const lang = params.lang as string;
  const { sendSearchEvent } = useGoogleAnalytics();
  const usesCompactProductGrid = useMediaQuery('(max-width: 1199px)');
  const initialVisibleProductCount = usesCompactProductGrid
    ? SEARCH_OVERLAY_INITIAL_PRODUCTS_COMPACT
    : SEARCH_OVERLAY_INITIAL_PRODUCTS_WIDE;

  const [query, setQuery] = useState('');
  const {
    groupedHits,
    groupedStores,
    isSearching,
    showDropdown,
    performSearch,
    handleClear: handleAutocompleteClear,
    handleFocus,
    closeDropdown,
    resetAutocomplete,
  } = useSearchAutocomplete(lang);

  // Collections for suggested searches - initialized from server-fetched data
  const [collections, setCollections] = useState<string[]>(initialCollections || []);

  // Products for the grid - initialized from server-fetched data
  const [products, setProducts] = useState<ProductCardData[]>(initialProducts || []);
  // Only show loading if we don't have initial data from server
  const [isLoadingProducts, setIsLoadingProducts] = useState(!initialProducts?.length);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(initialTotalProducts || 0);
  const [isDeferredContentVisible, setIsDeferredContentVisible] = useState(false);
  const [visibleProductCount, setVisibleProductCount] = useState(0);
  const visibleProducts = products.slice(0, visibleProductCount);
  const hasHiddenProducts = visibleProductCount < products.length;
  const hasMoreRemoteProducts = products.length < totalProducts;
  const hasMoreProducts = hasHiddenProducts || hasMoreRemoteProducts;

  // Track if we have server-fetched initial data
  const hasInitialData = Boolean(initialProducts?.length);

  // Update loading state when initial data arrives (for hydration)
  useEffect(() => {
    if (initialProducts?.length) {
      setIsLoadingProducts(false);
      setProducts(initialProducts);
      setTotalProducts(initialTotalProducts || 0);
    }
    if (initialCollections?.length) {
      setCollections(initialCollections);
    }
  }, [initialProducts, initialCollections, initialTotalProducts]);

  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  const isOpen = overlayStatus === 'OPEN';

  // Close dropdown when clicking outside of it
  useClickOutside(dropdownContainerRef, () => {
    if (showDropdown) {
      closeDropdown();
    }
  });

  // Close on route change
  useEffect(() => {
    if (isOpen) {
      searchStateActions.closeOverlay();
    }
  }, [pathname]);

  // Clear search state when overlay closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setIsDeferredContentVisible(false);
      setVisibleProductCount(0);
      resetAutocomplete();
      return;
    }

    const frame = requestAnimationFrame(() => {
      setIsDeferredContentVisible(true);
      setVisibleProductCount((current) =>
        current === 0 ? Math.min(initialVisibleProductCount, products.length) : current
      );
    });

    return () => cancelAnimationFrame(frame);
  }, [initialVisibleProductCount, isOpen, products.length, resetAutocomplete]);

  // Fallback: Fetch collections and initial products if not provided by server
  // This should only happen if server-side fetching failed
  useEffect(() => {
    if (isOpen && !hasInitialData) {
      fetchCollections();
      fetchInitialProducts();
    }
  }, [isOpen, lang, hasInitialData]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        searchStateActions.closeOverlay();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Fetch collection facet values for "Suggested Searches"
  const fetchCollections = async () => {
    try {
      const response = await fetch(`/api/facets/${lang}`);
      if (response.ok) {
        const json = await response.json();
        // Facets are nested under data.facets
        const collectionFacet = json.data?.facets?.collection || {};
        // Get all collection names (keys of the facet object)
        const collectionNames = Object.keys(collectionFacet);
        setCollections(collectionNames);
      }
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    }
  };

  // Fetch initial products (watches) for the grid - uses ISR-cached endpoint
  const fetchInitialProducts = async () => {
    setIsLoadingProducts(true);
    setCurrentPage(1);
    try {
      // Use ISR-cached endpoint for better performance
      const response = await fetch(
        `/api/products/overlay/${lang}?page=1&limit=${SEARCH_OVERLAY_PRODUCTS_PER_PAGE}`
      );

      if (response.ok) {
        const json = await response.json();
        const hits = json.data?.hits || [];
        const total = json.data?.totalHits || 0;
        const transformed = transformHitsToProductCards(hits, lang, (key) => tBadges(key));
        setProducts(transformed);
        setTotalProducts(total);
      }
    } catch (error) {
      console.error('Failed to fetch initial products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Load more products - uses ISR-cached endpoint
  const loadMoreProducts = async () => {
    if (isLoadingMore) return;

    if (hasHiddenProducts) {
      setVisibleProductCount((current) =>
        Math.min(current + initialVisibleProductCount, products.length)
      );
      return;
    }

    if (!hasMoreRemoteProducts) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      // Use ISR-cached endpoint for better performance
      const response = await fetch(
        `/api/products/overlay/${lang}?page=${nextPage}&limit=${SEARCH_OVERLAY_PRODUCTS_PER_PAGE}`
      );

      if (response.ok) {
        const json = await response.json();
        const hits = json.data?.hits || [];
        const transformed = transformHitsToProductCards(hits, lang, (key) => tBadges(key));
        setProducts((prev) => [...prev, ...transformed]);
        setVisibleProductCount((current) => current + transformed.length);
        setCurrentPage(nextPage);
      }
    } catch (error) {
      console.error('Failed to load more products:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleQueryChange = useCallback(
    (newQuery: string) => {
      setQuery(newQuery);
      performSearch(newQuery);
    },
    [performSearch]
  );

  const handleSubmit = useCallback(
    (searchQuery: string) => {
      if (searchQuery.trim()) {
        sendSearchEvent(searchQuery.trim());
        searchStateActions.closeOverlay();
        router.push(searchPaths.all(searchQuery));
      }
    },
    [router, sendSearchEvent]
  );

  const handleClear = useCallback(() => {
    setQuery('');
    handleAutocompleteClear();
  }, [handleAutocompleteClear]);

  const handleItemClick = useCallback((_hit: ProductHit) => {
    // Analytics tracking would go here
    searchStateActions.closeOverlay();
  }, []);

  const handleClose = useCallback(() => {
    searchStateActions.closeOverlay();
  }, []);

  const { addToCart, addingProductId } = useAddToCart({ itemListName: 'search' });

  // Close the overlay when adding so the cart drawer (opened by the add) is
  // visible over the page, matching the card-click behavior.
  const handleAddToBag = useCallback(
    (product: ProductCardData) => {
      void addToCart(product);
      searchStateActions.closeOverlay();
    },
    [addToCart]
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={overlayRef}
          className={styles.overlay}
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.25 }}
          data-testid="search-overlay"
        >
          {/* Close button - absolutely positioned top-right */}
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={t('aria.closeButton')}
          >
            <Icon name="close" size={24} />
          </button>

          <div ref={contentRef} className={styles.content}>
            {/* Search section - centered with max-width */}
            <div className={styles.searchSection}>
              {/* Title */}
              <h2 className={styles.title}>{cmsData?.title || t('overlay.title')}</h2>

              {/* Search input with dropdown */}
              <div ref={dropdownContainerRef} className={styles.searchContainer}>
                <SearchInput
                  defaultValue={query}
                  placeholder={cmsData?.placeholder}
                  onChange={handleQueryChange}
                  onSubmit={handleSubmit}
                  onClear={handleClear}
                  onFocus={() => handleFocus(query)}
                  autoFocus
                  variant="overlay"
                  size="lg"
                />

                {showDropdown && (
                  <SearchDropdown
                    groupedHits={groupedHits}
                    groupedStores={groupedStores}
                    query={query}
                    onItemClick={handleItemClick}
                    onClose={handleClose}
                    isLoading={isSearching}
                    isVisible={showDropdown}
                    className={styles.dropdown}
                  />
                )}
              </div>

              {isDeferredContentVisible && (
                <>
                  {/* Retailers link */}
                  {cmsData?.showRetailers !== false && (
                    <div className={styles.retailerLink}>
                      <Icon name="pin" size={20} />
                      <Link href={searchPaths.stores()} onClick={handleClose}>
                        {t('retailers')}
                      </Link>
                    </div>
                  )}

                  {/* Suggested Search label */}
                  <p className={styles.suggestedLabel}>{t('suggestedSearch')}</p>

                  {/* Collections list */}
                  {collections.length > 0 && (
                    <div className={styles.collectionsWrapper}>
                      {collections.map((collection) => (
                        <Link
                          key={collection}
                          href={searchPaths.watches(collection)}
                          className={styles.collectionLink}
                          onClick={handleClose}
                        >
                          {collection}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Products grid */}
            {isDeferredContentVisible && (
              <div className={styles.productsSection}>
                {isLoadingProducts ? (
                  <div className={styles.loading}>{/* Loading spinner would go here */}</div>
                ) : (
                  <>
                    <div className={styles.productsGrid}>
                      {visibleProducts.map((product) => (
                        <ProductCard
                          key={product.sku}
                          product={product}
                          onClick={handleClose}
                          onAddToBag={handleAddToBag}
                          isAddingToBag={addingProductId === getAddToCartTrackingId(product)}
                          {...getGalletPlpProductCardProps('Watch')}
                        />
                      ))}
                    </div>

                    {hasMoreProducts && (
                      <div className={styles.showMoreWrapper}>
                        <Button
                          variant="primary"
                          onClick={loadMoreProducts}
                          loading={isLoadingMore}
                        >
                          {t('overlay.showMore')}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

SearchOverlay.displayName = 'SearchOverlay';
